import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Shield, Clock, MessageCircle, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ContactVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  onVerificationComplete: (verificationType: 'email' | 'phone', isReset?: boolean) => void;
}

export default function ContactVerificationDialog({ 
  isOpen, 
  onClose, 
  profile,
  onVerificationComplete 
}: ContactVerificationDialogProps) {
  const [activeTab, setActiveTab] = useState<"email" | "phone">("email");
  const [step, setStep] = useState<"input" | "verify">("input");
  const [email, setEmail] = useState(profile?.email || "");
  const [phone, setPhone] = useState(profile?.phone === "PHONE_REQUIRED" ? "" : profile?.phone || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  // Removed WhatsApp verification method - only SMS now with MojoAuth
  const { toast } = useToast();

  const formatPhoneNumber = (value: string) => {
    // For UK numbers, just clean and return without formatting
    const phoneNumber = value.replace(/\D/g, "");
    return phoneNumber;
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string) => {
    // Clean phone number of spaces and formatting
    const cleanPhone = phone.replace(/\s/g, '');
    
    // UK phone number validation - more comprehensive
    // Mobile: 07XXXXXXXXX (11 digits total)
    // Landline: 01XXXXXXXXX, 02XXXXXXXXX (11 digits total) 
    // International: +447XXXXXXXXX
    const ukPhoneRegex = /^(?:(?:\+44\s?7\d{8,9})|(?:07\d{8,9})|(?:(?:\+44\s?[1235689]\d{8,9})|(?:0[1235689]\d{8,9})))$/;
    
    console.log('Phone validation:', { 
      originalPhone: phone, 
      cleanPhone, 
      isValid: ukPhoneRegex.test(cleanPhone),
      length: cleanPhone.length 
    });
    
    return ukPhoneRegex.test(cleanPhone);
  };

  const requestVerification = async () => {
    console.log("=== SMS VERIFICATION DEBUG START ===");
    console.log("requestVerification called", { activeTab, email, phone });
    
    // Check if already verified
    if (activeTab === "email" && profile?.email_verified) {
      console.log("Email already verified, aborting");
      toast({
        title: "Email Already Verified",
        description: "Your email address is already verified.",
        variant: "default",
      });
      return;
    }

    if (activeTab === "phone" && profile?.phone_verified) {
      console.log("Phone already verified, aborting");
      toast({
        title: "Phone Already Verified", 
        description: "Your phone number is already verified.",
        variant: "default",
      });
      return;
    }
    
    if (activeTab === "email" && !validateEmail(email)) {
      console.log("Email validation failed", { email });
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (activeTab === "phone" && !validatePhone(phone)) {
      console.log("Phone validation failed", { phone });
      toast({
        title: "Invalid Phone",
        description: "Please enter a valid UK phone number (e.g., 07123456789 or 01234567890).",
        variant: "destructive",
      });
      return;
    }

    console.log("All validations passed, proceeding with verification request");
    setIsLoading(true);

    try {
      console.log("Getting session for verification request");
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      console.log("Session obtained", { hasSession: !!session, hasToken: !!accessToken });

      if (activeTab === "phone") {
        // Use secure verify-contact function for phone verification request
        const requestBody = {
          type: 'phone',
          action: 'request',
          phone: phone.replace(/\D/g, "")
        };
        console.log("Invoking verify-contact function for phone", { requestBody });

        const { data, error } = await supabase.functions.invoke('verify-contact', {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          body: requestBody
        });

        console.log("Function response", { data, error });

        if (error) throw error;

        setStep("verify");
        setTimeLeft(600); // 10 minutes
        
        const timer = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        toast({
          title: "Verification SMS Sent",
          description: `A verification code has been sent to ${phone}`,
        });

      } else {
        // Use existing email verification flow
        const requestBody = {
          type: 'email',
          action: 'request',
          email
        };
        console.log("Invoking verify-contact function for email", { requestBody });

        const { data, error } = await supabase.functions.invoke('verify-contact', {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          body: requestBody
        });

        console.log("Function response", { data, error });

        if (error) throw error;

        setStep("verify");
        setTimeLeft(600); // 10 minutes
        
        const timer = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        toast({
          title: "Verification Code Sent",
          description: "A verification code has been sent to your email.",
        });
      }

    } catch (error: any) {
      console.error("Error requesting verification:", error);

      // Handle rate limit gracefully with a 60s cooldown
      const msg = error?.message || "Failed to send verification code.";
      if (/rate limit exceeded/i.test(msg)) {
        setCooldown(60);
        const cd = setInterval(() => {
          setCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(cd);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }

      toast({
        title: "Error",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a valid 6-digit verification code.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (activeTab === "phone") {
        // Delegate verification to secure edge function (service role) to bypass protected fields trigger
        const { error } = await supabase.functions.invoke('verify-contact', {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          body: {
            type: 'phone',
            action: 'verify',
            code: verificationCode,
            phone: phone?.replace(/\D/g, '') || undefined
          }
        });

        if (error) throw error;
      } else {
        // Use verify-contact for email verification
        const { error } = await supabase.functions.invoke('verify-contact', {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          body: {
            type: 'email',
            action: 'verify',
            code: verificationCode
          }
        });

        if (error) throw error;
      }

      console.log('Verification successful, calling onVerificationComplete');
      
      toast({
        title: "Verification Successful",
        description: `Your ${activeTab} has been verified successfully.`,
      });

      // Call the completion callback to refresh the profile data
      onVerificationComplete(activeTab);
      onClose();
      setStep("input");
      setVerificationCode("");

    } catch (error: any) {
      console.error("Error verifying code:", error);
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid verification code.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetEmailVerification = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('reset-email-verification', {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: { action: 'reset' }
      });

      if (error) throw error;

      toast({
        title: "Email Unverified",
        description: "Email verification status has been reset.",
      });

      setStep("input");
      setVerificationCode("");
      onVerificationComplete('email', true);
    } catch (error: any) {
      console.error("Reset verification error:", error);
      toast({
        title: "Reset Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-md mx-2 sm:mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Verify Contact Information
          </DialogTitle>
          <DialogDescription>
            Verify your email and phone number for enhanced security and communication.
          </DialogDescription>
          
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "email" | "phone")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
              {profile?.email_verified && <Badge variant="secondary" className="text-xs">Verified</Badge>}
            </TabsTrigger>
            <TabsTrigger value="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone
              {profile?.phone_verified && <Badge variant="secondary" className="text-xs">Verified</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-4">
            {profile?.email_verified ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-2 p-4 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-700 font-medium">Email Verified</span>
                </div>
                
                {/* Verification Management Controls */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 mb-3 font-medium">Manage Email Verification:</p>
                  <div className="space-y-2">
                    <Button 
                      onClick={resetEmailVerification}
                      variant="outline" 
                      size="sm"
                      className="w-full bg-red-100 hover:bg-red-200 text-red-800 border-red-300"
                    >
                      Reset Email Verification
                    </Button>
                    <p className="text-xs text-blue-600">
                      Reset to re-verify your email address with a new code
                    </p>
                  </div>
                </div>
              </div>
            ) : step === "input" ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                  />
                </div>
                <Button 
                  onClick={requestVerification} 
                  disabled={isLoading || !validateEmail(email) || cooldown > 0}
                  className="w-full"
                >
                  {isLoading ? "Sending..." : cooldown > 0 ? `Please wait ${formatTime(cooldown)}` : "Send Verification Code"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code sent to <strong>{email}</strong>
                  </p>
                  {timeLeft > 0 && (
                    <div className="flex items-center justify-center gap-1 mt-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Code expires in {formatTime(timeLeft)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailCode">Verification Code</Label>
                  <Input
                    id="emailCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep("input")} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    onClick={verifyCode} 
                    disabled={isLoading || verificationCode.length !== 6 || timeLeft === 0}
                    className="flex-1"
                  >
                    {isLoading ? "Verifying..." : "Verify"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="phone" className="space-y-4">
            {profile?.phone_verified ? (
              <div className="flex items-center justify-center space-x-2 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-700 font-medium">Phone Verified</span>
              </div>
            ) : step === "input" ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g., 07123456789 or 01234567890"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Verification Method</Label>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium">SMS Verification</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      A verification code will be sent to your phone via SMS
                    </p>
                  </div>
                </div>
                
                <Button 
                  onClick={requestVerification} 
                  disabled={isLoading || !validatePhone(phone) || cooldown > 0}
                  aria-busy={isLoading}
                  title={!validatePhone(phone) ? "Enter a valid UK phone number" : (cooldown > 0 ? `Please wait ${formatTime(cooldown)}` : '')}
                  className="w-full"
                  data-edge-action="request-sms"
                >
                  {isLoading ? "Sending..." : cooldown > 0 ? `Please wait ${formatTime(cooldown)}` : "Send Verification Code"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code sent to <strong>{phone}</strong>
                  </p>
                  {timeLeft > 0 && (
                    <div className="flex items-center justify-center gap-1 mt-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Code expires in {formatTime(timeLeft)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneCode">Verification Code</Label>
                  <Input
                    id="phoneCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep("input")} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    onClick={verifyCode} 
                    disabled={isLoading || verificationCode.length !== 6 || timeLeft === 0}
                    className="flex-1"
                  >
                    {isLoading ? "Verifying..." : "Verify"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}